// MSQA photos shared by the phone form (msqa.ts) and the desktop review
// (msqaDesktop.ts): downscaling, and adding photos to an already-SUBMITTED
// form (row #8, 2026-10-16).
//
// A submitted form is locked; photos added afterwards are an audited
// amendment of the item's photo list (POST /api/msqa/forms/<id>/amend, path
// answers.<item>.photos), never an unlock. Each photo is first uploaded as a
// field capture (form_kind msqa_photo) of the form's project - the same
// pipeline as the phone's photos (faces / plates blurred, EXIF stripped) -
// with the sha256 computed here; the server refuses a ref that isn't this
// project's upload or whose file hash differs, chains the amendment's hash
// and regenerates the PDFs as a new revision. Online only (not queued): the
// caller shows the error.
import { getJson, postJson } from "../api";
import { blobSha256 } from "./msqaCanonical";
import { newUuid } from "./outbox";

export async function downscale(file: File, longEdge = 2560): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
    const s = Math.min(1, longEdge / Math.max(bmp.width, bmp.height));
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * s);
    c.height = Math.round(bmp.height * s);
    c.getContext("2d")!.drawImage(bmp, 0, 0, c.width, c.height);
    return await new Promise<Blob>((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("encode"))), "image/jpeg", 0.85));
  } catch {
    return file;
  }
}

interface PhotoRef {
  ref: string;
  caption?: string;
  sha256?: string;
}

export interface AddPhotosArgs {
  formId: string;
  item: string;
  files: File[];
  caption?: string;
  reason: string;
  onProgress?: (text: string) => void;
}

/** Upload `files` and record one amendment adding them to `item`. Returns the
 * number of photos added. Throws (with the server's message) on failure. */
export async function addPhotosToSubmitted(a: AddPhotosArgs): Promise<number> {
  if (!a.files.length) throw new Error("Choose one or more photos first.");
  if (!a.reason.trim()) throw new Error("Give a reason for the amendment.");
  // the form as it stands now (after any earlier amendments): its project, title and the item's photos
  const form = await getJson<{ project_id: string; short_title: string; status: string;
    content: { answers?: Record<string, { photos?: PhotoRef[] }> } }>(`/api/msqa/forms/${encodeURIComponent(a.formId)}`);
  if (form.status !== "submitted") throw new Error("Only a submitted form takes photos this way - a draft is edited on the phone.");
  const added: PhotoRef[] = [];
  for (const [i, file] of a.files.entries()) {
    a.onProgress?.(`Uploading ${i + 1} of ${a.files.length}…`);
    const blob = file.size > 2.5 * 1024 * 1024 ? await downscale(file) : file;
    const ref = newUuid();
    const sha = await blobSha256(blob);
    const body = new FormData();
    body.append("meta", JSON.stringify({
      client_uuid: ref, form_kind: "msqa_photo", workspace: "construction", project_id: form.project_id,
      captured_at: new Date(file.lastModified || Date.now()).toISOString(), lat: null, lon: null, accuracy_m: null,
      heading_deg: null, heading_source: "none", device: navigator.userAgent.slice(0, 300),
      notes: `MSQA ${form.short_title} ${a.item} (added after submission)`.slice(0, 400), form: { msqa_form: a.formId, item_key: a.item },
    }));
    body.append("photo_headings", JSON.stringify([null]));
    body.append("photos", blob, `photo-${i + 1}.jpg`);
    const res = await fetch("/api/field/captures", { method: "POST", body, credentials: "same-origin" });
    if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { detail?: string }).detail ?? `Upload failed (${res.status})`);
    added.push({ ref, caption: a.caption?.trim() || undefined, sha256: sha ?? undefined });
  }
  a.onProgress?.("Recording the amendment…");
  const existing = form.content.answers?.[a.item]?.photos ?? [];
  await postJson(`/api/msqa/forms/${encodeURIComponent(a.formId)}/amend`, {
    reason: a.reason.trim(), changes: [{ path: `answers.${a.item}.photos`, value: [...existing, ...added] }],
  });
  return added.length;
}
